import type { ViewspecState } from "./types.ts";

/**
 * NLS single-letter viewspec codes.
 * Each letter maps to a partial ViewspecState change.
 */
export const VIEWSPEC_CODES: Record<string, Partial<ViewspecState>> = {
  // Depth control
  a: { depthLimit: null },       // All levels
  b: { depthLimit: 1 },          // Branch tops only
  c: { depthLimit: 2 },          // Two levels
  d: { depthLimit: 3 },          // Three levels

  // Content display
  e: { showContent: "all" },     // Everything
  f: { showContent: "first-line" }, // First line only
  g: { showContent: "none" },    // No content (statement list)

  // SID display
  h: { showSIDs: true },         // Show SIDs
  i: { showSIDs: false },        // Hide SIDs

  // Numbering
  j: { numbering: "sid" },       // Hierarchical numbering
  k: { numbering: "sequential" }, // Sequential (1, 2, 3...)
  l: { numbering: "none" },      // No numbering

  // Layout
  m: { indentation: true },      // Indented view
  n: { indentation: false },     // Flat view
  o: { showBlankLines: true },   // Spaced
  p: { showBlankLines: false },  // Compact

  // Split screen
  q: { splitMode: "horizontal" }, // Horizontal split
  r: { splitMode: "vertical" },   // Vertical split
  s: { splitMode: "none" },       // No split
  t: { splitLinked: true },       // Link panes
  u: { splitLinked: false },      // Unlink panes
};

/**
 * Parse a viewspec codes string into a partial ViewspecState.
 * E.g., "bhm" → { depthLimit: 1, showSIDs: true, indentation: true }
 */
export function parseViewspecCodes(
  codes: string
): Partial<ViewspecState> {
  const result: Partial<ViewspecState> = {};
  for (const ch of codes.toLowerCase()) {
    const spec = VIEWSPEC_CODES[ch];
    if (spec) Object.assign(result, spec);
  }
  return result;
}

/**
 * Get the active viewspec code letters from a ViewspecState.
 */
export function getActiveViewspecCodes(vs: ViewspecState): string {
  let codes = "";

  // Depth
  if (vs.depthLimit === null) codes += "a";
  else if (vs.depthLimit === 1) codes += "b";
  else if (vs.depthLimit === 2) codes += "c";
  else if (vs.depthLimit === 3) codes += "d";

  // Content
  if (vs.showContent === "all") codes += "e";
  else if (vs.showContent === "first-line") codes += "f";
  else if (vs.showContent === "none") codes += "g";

  // SIDs
  codes += vs.showSIDs ? "h" : "i";

  // Numbering
  if (vs.numbering === "sid") codes += "j";
  else if (vs.numbering === "sequential") codes += "k";
  else codes += "l";

  // Layout
  codes += vs.indentation ? "m" : "n";
  codes += vs.showBlankLines ? "o" : "p";

  // Split
  if (vs.splitMode === "horizontal") codes += "q";
  else if (vs.splitMode === "vertical") codes += "r";
  else codes += "s";

  codes += vs.splitLinked ? "t" : "u";

  return codes;
}
